/**
 * ContactSensorModalClassic — Monolithic contact/door sensor modal template.
 *
 * Read-only contact sensor display panel:
 * - Status: large Open/Closed display with door icon
 *
 * No commands — purely observational.
 */
import { useCallback, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import './ContactSensorModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const CONTACT_SENSOR_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  // ── Modal Background (header + overall) ──
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name Text', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  // ── Status Section ──
  { id: 'status', label: 'Contact Status', icon: 'sensor_door', slots: [
    { id: 'status-icon', label: 'Door Icon', editableProps: ['fontSize', 'color'],
      states: [{ id: 'open', label: 'Open' }, { id: 'closed', label: 'Closed' }] },
    { id: 'status-value', label: 'Status Text "OPEN/CLOSED"', editableProps: ['fontSize', 'color', 'fontWeight'],
      states: [{ id: 'open', label: 'Open' }, { id: 'closed', label: 'Closed' }] },
    { id: 'status-badge', label: 'Status Badge Pill', editableProps: ['background', 'color', 'borderRadius', 'padding', 'fontSize'],
      states: [{ id: 'open', label: 'Open' }, { id: 'closed', label: 'Closed' }] },
    { id: 'status-hint', label: 'Hint Text', editableProps: ['fontSize', 'color'] },
  ]},
  // ── Operation Log Section ──
  { id: 'log', label: 'Operation Log', icon: 'history', slots: [
    { id: 'log-title', label: 'Title "OPERATION LOG"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'log-entry', label: 'Log Entry Row', editableProps: ['fontSize', 'color', 'padding', 'borderColor'] },
    { id: 'log-entry-icon', label: 'Entry Status Icon', editableProps: ['fontSize', 'color'] },
    { id: 'log-entry-status', label: 'Entry Status Text', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'log-entry-time', label: 'Entry Timestamp', editableProps: ['fontSize', 'color'] },
  ]},
]

const DEFAULT_SECTION_ORDER = CONTACT_SENSOR_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Mock log entries for editor preview
const MOCK_LOGS = [
  { timestamp: new Date(Date.now() - 5 * 60000).toISOString(), action: 'open', source: 'user' },
  { timestamp: new Date(Date.now() - 12 * 60000).toISOString(), action: 'close', source: 'user' },
  { timestamp: new Date(Date.now() - 45 * 60000).toISOString(), action: 'open', source: 'automation' },
  { timestamp: new Date(Date.now() - 2 * 3600000).toISOString(), action: 'close', source: 'user' },
  { timestamp: new Date(Date.now() - 3 * 3600000).toISOString(), action: 'open', source: 'user' },
  { timestamp: new Date(Date.now() - 5 * 3600000).toISOString(), action: 'close', source: 'schedule' },
  { timestamp: new Date(Date.now() - 8 * 3600000).toISOString(), action: 'open', source: 'user' },
  { timestamp: new Date(Date.now() - 12 * 3600000).toISOString(), action: 'close', source: 'automation' },
]

function formatLogTime(iso: string): string {
  try {
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 1) return 'Just now'
    if (diffMin < 60) return `${diffMin}m ago`
    const diffHr = Math.floor(diffMin / 60)
    if (diffHr < 24) return `${diffHr}h ago`
    const diffDay = Math.floor(diffHr / 24)
    if (diffDay < 7) return `${diffDay}d ago`
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch {
    return iso
  }
}

export function ContactSensorModalClassic({
  device,
  deviceState,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
  operationLogs,
}: DeviceModalTemplateProps) {
  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const rawContact = getAttr(deviceState, 'contact', 'doorOpen', 'isOpen')
  // contact=true / doorOpen=true / isOpen=true all mean OPEN
  // When contact attr means "contact made" (closed), invert: contact=false → open
  // We normalise to isOpen: doorOpen/isOpen keys are directly "is open?"
  // contact key in Zigbee means "contact made" = CLOSED, so contact=true → closed
  const isOpen = (() => {
    if (rawContact === undefined || rawContact === null) return isEditorMode ? false : null
    const raw = String(rawContact).toLowerCase()
    // If key is 'contact': true = closed (contact made), false = open
    if ('contact' in (deviceState ?? {}) && !('doorOpen' in (deviceState ?? {})) && !('isOpen' in (deviceState ?? {}))) {
      return raw === 'false' || rawContact === false || rawContact === 0
    }
    // doorOpen / isOpen: true = open
    return raw === 'true' || rawContact === true || rawContact === 1
  })()

  return (
    <div
      className={`contact-sensor-modal-classic${isOpen ? ' is-open' : ' is-closed'}${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="contact-sensor-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}

      {/* Header (always first, not reorderable) */}
      <div className="contact-sensor-modal-classic__header">
        <button className="contact-sensor-modal-classic__back" onClick={onClose} aria-label="Back">
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="contact-sensor-modal-classic__title" data-modal-slot="header-title">{device.name || 'Contact Sensor'}</h2>
        <button
          className="contact-sensor-modal-classic__edit"
          onClick={() => {/* future: edit nickname */}}
          aria-label="Edit"
        >
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
      </div>

      {/* Sections rendered in order */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'status': {
            const stateKey = isOpen === null ? 'closed' : isOpen ? 'open' : 'closed'
            return (
              <div key="status" className={`contact-sensor-modal-classic__status${hl}`} data-modal-section="status">
                <div className="contact-sensor-modal-classic__status-inner">
                  <div className={`contact-sensor-modal-classic__status-ring${isOpen ? ' open' : ' closed'}`}>
                    <span
                      className="material-symbols-rounded contact-sensor-modal-classic__status-icon"
                      data-modal-slot="status-icon"
                      data-modal-slot-state={stateKey}
                    >
                      {isOpen ? 'sensor_door' : 'door_front'}
                    </span>
                  </div>
                  <span
                    className={`contact-sensor-modal-classic__status-value${isOpen ? ' open' : ' closed'}`}
                    data-modal-slot="status-value"
                    data-modal-slot-state={stateKey}
                  >
                    {isOpen === null ? '—' : isOpen ? 'OPEN' : 'CLOSED'}
                  </span>
                  <span
                    className={`contact-sensor-modal-classic__status-badge${isOpen ? ' open' : ' closed'}`}
                    data-modal-slot="status-badge"
                    data-modal-slot-state={stateKey}
                  >
                    {isOpen === null ? 'Unknown' : isOpen ? 'Access possible' : 'Secured'}
                  </span>
                  <span className="contact-sensor-modal-classic__status-hint" data-modal-slot="status-hint">
                    Door / Window Contact Sensor
                  </span>
                </div>
              </div>
            )
          }
          case 'log': {
            // Use real logs if available, mock for editor preview
            const logs = operationLogs?.length ? operationLogs.slice(0, 50) : (isEditorMode ? MOCK_LOGS : [])
            return (
              <div key="log" className={`contact-sensor-modal-classic__log${hl}`} data-modal-section="log">
                <div className="contact-sensor-modal-classic__log-title" data-modal-slot="log-title">OPERATION LOG</div>
                {logs.length === 0 ? (
                  <div className="contact-sensor-modal-classic__log-empty">No log entries</div>
                ) : (
                  <div className="contact-sensor-modal-classic__log-list">
                    {logs.map((entry, idx) => {
                      const isEntryOpen = entry.action === 'open'
                      return (
                        <div
                          key={`log-${idx}`}
                          className="contact-sensor-modal-classic__log-entry"
                          data-modal-slot="log-entry"
                        >
                          <span
                            className={`material-symbols-rounded contact-sensor-modal-classic__log-entry-icon ${isEntryOpen ? 'open' : 'closed'}`}
                            data-modal-slot="log-entry-icon"
                          >
                            {isEntryOpen ? 'sensor_door' : 'door_front'}
                          </span>
                          <span
                            className={`contact-sensor-modal-classic__log-entry-status ${isEntryOpen ? 'open' : 'closed'}`}
                            data-modal-slot="log-entry-status"
                          >
                            {isEntryOpen ? 'Opened' : 'Closed'}
                          </span>
                          <span className="contact-sensor-modal-classic__log-entry-time" data-modal-slot="log-entry-time">
                            {formatLogTime(entry.timestamp)}
                          </span>
                          {entry.source && (
                            <span className="contact-sensor-modal-classic__log-entry-source">
                              {entry.source}
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          }
          default:
            return null
        }
      })}
    </div>
  )
}
